import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2, Check, X, Users, Search, Info, ClipboardList, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const LEVELS = [
  { key: "forte", color: "#10b981", ring: "ring-emerald-500/30", bg: "bg-emerald-500/10", tx: "text-emerald-300" },
  { key: "pertinente", color: "#0ea5e9", ring: "ring-sky-500/30", bg: "bg-sky-500/10", tx: "text-sky-300" },
  { key: "possible", color: "#94a3b8", ring: "ring-white/15", bg: "bg-white/5", tx: "text-slate-300" },
];

const PRESENCES = ["present", "absent", "excuse"];
const EFFECTS = ["tres_positif", "positif", "neutre", "difficile", "refus"];

export default function Recommandations() {
  const { t } = useTranslation();
  const [acts, setActs] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [rows, setRows] = useState([]);
  const [maisonId, setMaisonId] = useState("");
  const [actId, setActId] = useState("");
  const [reco, setReco] = useState(null);
  const [picks, setPicks] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [bilanDate, setBilanDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [part, setPart] = useState({});
  const [actSug, setActSug] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [cat, trk] = await Promise.all([api.get("/planning/catalog"), api.get("/pp/tracking")]);
        setActs((cat.data.activities || []).filter((a) => a.active !== false));
        const r = trk.data.rows || []; setRows(r);
        const mp = {};
        r.forEach((x) => { if (x.maison_id) mp[x.maison_id] = x.maison || x.maison_id; });
        const opts = Object.entries(mp).map(([id, name]) => ({ id, name }));
        setMaisons(opts);
        if (opts.length) setMaisonId(opts[0].id);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    })();
  }, []);

  const actOptions = useMemo(
    () => acts.filter((a) => !a.maison_id || a.maison_id === maisonId),
    [acts, maisonId]
  );

  const run = async (aid, mid) => {
    if (!aid || !mid) { setReco(null); return; }
    setLoading(true);
    try {
      const [{ data }, pk] = await Promise.all([
        api.post("/pp/recommendations", { catalog_id: aid, maison_id: mid }),
        api.get("/pp/recommendations/picks", { params: { activity_key: aid, maison_id: mid } }),
      ]);
      setReco({ ...data, recommendations: (data.recommendations || []).map((r) => ({ ...r, id: r.resident_id })) });
      const pmap = {}; (pk.data.picks || []).forEach((p) => { pmap[p.resident_id] = p.decision; });
      setPicks(pmap);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setReco(null); }
    setLoading(false);
  };

  const onSelectActivity = (id) => { setActId(id); run(id, maisonId); };
  const onSelectMaison = (id) => { setMaisonId(id); setActId(""); setReco(null); };

  const decide = async (resident_id, decision) => {
    try {
      await api.post("/pp/recommendations/picks", { activity_key: actId, maison_id: maisonId, resident_id, decision });
      setPicks((p) => ({ ...p, [resident_id]: decision }));
      toast.success(t(decision === "add" ? "pp.reco.added" : "pp.reco.skipped"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const recommendedIds = new Set((reco?.recommendations || []).map((r) => r.resident_id));
  const manualCandidates = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((r) => r.maison_id === maisonId && r.status !== "sorti" && !recommendedIds.has(r.id) &&
      (!qq || (r.full_name || "").toLowerCase().includes(qq)));
  }, [rows, maisonId, q, reco]); // eslint-disable-line

  const nameById = useMemo(() => {
    const m = {};
    (reco?.recommendations || []).forEach((r) => { m[r.resident_id] = { name: r.full_name, room: r.room, goal: r.reasons?.[0]?.objective_id, goalTitle: r.reasons?.[0]?.objective_title }; });
    rows.forEach((r) => { if (!m[r.id]) m[r.id] = { name: r.full_name, room: r.room }; });
    return m;
  }, [reco, rows]);
  const retainedIds = Object.keys(picks).filter((id) => picks[id] === "add");

  useEffect(() => {
    if (!actId || !maisonId) return;
    (async () => {
      try {
        const { data } = await api.get("/pp/participation", { params: { activity_key: actId, maison_id: maisonId, activity_date: bilanDate } });
        const mp = {};
        (data.participation || []).forEach((p) => { mp[p.resident_id] = { presence: p.presence, effect: p.effect || "", note: p.note || "", saved: true }; });
        setPart(mp);
      } catch { /* noop */ }
    })();
  }, [actId, bilanDate, maisonId]);

  const setP = (rid, patch) => setPart((p) => ({ ...p, [rid]: { ...(p[rid] || { presence: "present", effect: "", note: "" }), ...patch } }));
  const savePart = async (rid) => {
    const p = part[rid] || { presence: "present", effect: "", note: "" };
    const info = nameById[rid] || {};
    try {
      await api.post("/pp/participation", {
        activity_key: actId, activity_name: reco?.activity_name || "", activity_date: bilanDate,
        maison_id: maisonId, resident_id: rid, resident_name: info.name || "",
        presence: p.presence, effect: p.presence === "present" ? (p.effect || null) : null,
        note: p.note || "", linked_goal_id: info.goal || null,
      });
      setP(rid, { saved: true }); toast.success(t("pp.bilan.saved"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggleActions = async (rid, gid) => {
    const cur = actSug[rid];
    if (cur?.open) { setActSug((s) => ({ ...s, [rid]: { ...cur, open: false } })); return; }
    setActSug((s) => ({ ...s, [rid]: { open: true, loading: true, actions: [] } }));
    try {
      const { data } = await api.get(`/pp/goals/${gid}/actions`);
      setActSug((s) => ({ ...s, [rid]: { open: true, loading: false, actions: (data.actions || []).filter((a) => a.status !== "realisee") } }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setActSug((s) => ({ ...s, [rid]: { open: false } })); }
  };
  const markActionDone = async (rid, gid, aid) => {
    const p = part[rid] || { presence: "present", effect: "", note: "" };
    const info = nameById[rid] || {};
    try {
      await api.patch(`/pp/goals/${gid}/actions/${aid}`, { status: "realisee" });
      await api.post("/pp/participation", {
        activity_key: actId, activity_name: reco?.activity_name || "", activity_date: bilanDate,
        maison_id: maisonId, resident_id: rid, resident_name: info.name || "",
        presence: p.presence, effect: p.presence === "present" ? (p.effect || null) : null,
        note: p.note || "", linked_goal_id: gid, linked_action_id: aid,
      });
      setActSug((s) => ({ ...s, [rid]: { ...s[rid], actions: (s[rid]?.actions || []).filter((a) => a.id !== aid) } }));
      toast.success(t("pp.bilan.markedDone"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const selectedAct = acts.find((a) => a.id === actId);

  const Card = ({ r }) => {
    const decided = picks[r.id];
    return (
      <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-4" data-testid={`reco-card-${r.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-100">{r.full_name}</div>
            {r.room && <div className="text-xs text-slate-500">{t("pp.reco.room")} {r.room}</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => decide(r.id, "add")} data-testid={`reco-add-${r.id}`}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition ${decided === "add" ? "bg-emerald-500 text-slate-900" : "bg-white/10 text-slate-200 hover:bg-emerald-500/20"}`}>
              <Check className="h-3.5 w-3.5" />{decided === "add" ? t("pp.reco.added") : t("pp.reco.add")}
            </button>
            <button onClick={() => decide(r.id, "skip")} data-testid={`reco-skip-${r.id}`}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition ${decided === "skip" ? "bg-red-500/70 text-white" : "bg-white/10 text-slate-200 hover:bg-red-500/20"}`}>
              <X className="h-3.5 w-3.5" />{decided === "skip" ? t("pp.reco.skipped") : t("pp.reco.skip")}
            </button>
          </div>
        </div>
        <div className="mt-2 border-t border-white/5 pt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><Info className="h-3 w-3" />{t("pp.reco.why")}</div>
          <ul className="space-y-1">
            {r.reasons.map((rs, i) => (
              <li key={i} className="text-sm text-slate-300">
                <span className="text-slate-100 font-medium">{rs.objective_title}</span>
                {rs.domain && <span className="text-slate-400"> · {t("pp.reco.domainLbl")}: {rs.domain}</span>}
                <span className="text-slate-400"> · {t("pp.reco.priority")}: {t(`pp.priorityLbl.${rs.priority}`, { defaultValue: rs.priority })}</span>
                {rs.matched_keywords?.length > 0 && <span className="text-amber-300"> · {t("pp.reco.keywordsLbl")}: {rs.matched_keywords.join(", ")}</span>}
                {rs.actions?.length > 0 && <div className="text-slate-400 text-xs mt-0.5">{t("pp.reco.actionLbl")} : {rs.actions[0]}</div>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a1721] text-slate-100 p-4 sm:p-6" data-testid="reco-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-amber-400"><Sparkles className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">CarePilot</span></div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">{t("pp.reco.title")}</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">{t("pp.reco.subtitle")}</p>

        <div className="flex flex-wrap gap-3 mb-6">
          {maisons.length > 1 && (
            <select value={maisonId} onChange={(e) => onSelectMaison(e.target.value)} data-testid="reco-maison"
              className="h-11 rounded-xl bg-white/[0.04] border border-white/10 px-3 text-sm">
              {maisons.map((m) => <option key={m.id} value={m.id} className="bg-[#0f2130]">{m.name}</option>)}
            </select>
          )}
          <select value={actId} onChange={(e) => onSelectActivity(e.target.value)} data-testid="reco-activity"
            className="h-11 min-w-[240px] flex-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 text-sm">
            <option value="" className="bg-[#0f2130]">{t("pp.reco.selectActivity")}</option>
            {actOptions.map((a) => <option key={a.id} value={a.id} className="bg-[#0f2130]">{a.name_fr}</option>)}
          </select>
        </div>

        {selectedAct && (
          <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3 mb-4 text-sm">
            <span className="text-slate-400">{t("pp.reco.activityTags")} : </span>
            {(selectedAct.domains || []).length === 0 && (selectedAct.keywords || []).length === 0 ? (
              <span className="text-slate-500">{t("pp.reco.noTags")}</span>
            ) : (
              <span className="inline-flex flex-wrap gap-1.5 align-middle">
                {(selectedAct.domains || []).map((d) => <span key={d} className="rounded-full bg-teal-500/15 text-teal-300 px-2 py-0.5 text-xs">{d}</span>)}
                {(selectedAct.keywords || []).map((k) => <span key={k} className="rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5 text-xs">{k}</span>)}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-24 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /></div>
        ) : !reco ? (
          <div className="py-24 text-center text-slate-500" data-testid="reco-empty">{t("pp.reco.noActivity")}</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {LEVELS.map((l) => (
                <span key={l.key} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${l.bg} ${l.tx} ring-1 ${l.ring}`}>
                  <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />{t(`pp.reco.${l.key}`)} · {reco.counts[l.key] || 0}
                </span>
              ))}
              <span className="text-xs text-slate-500 self-center ml-auto">{reco.total_present} {t("pp.reco.present")}</span>
            </div>

            {(reco.recommendations || []).length === 0 ? (
              <div className="py-16 text-center text-slate-500" data-testid="reco-none">{t("pp.reco.noReco")}</div>
            ) : (
              <div className="space-y-6">
                {LEVELS.map((l) => {
                  const items = reco.recommendations.filter((r) => r.level === l.key);
                  if (!items.length) return null;
                  return (
                    <div key={l.key} data-testid={`reco-group-${l.key}`}>
                      <h2 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: l.color }}>
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: l.color }} />{t(`pp.reco.${l.key}`)}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {items.map((r) => <Card key={r.id} r={r} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Autres habitants — potentiellement intéressés (pas d'objectif PP directement lié) */}
            <div className="mt-8 rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-4" data-testid="reco-manual">
              <div className="flex items-center gap-2 mb-1.5 text-sm font-semibold text-slate-300"><Users className="h-4 w-4 text-slate-400" />{t("pp.reco.othersGroup")}</div>
              <p className="text-[11px] text-slate-500 mb-3 flex items-start gap-1.5"><Info className="h-3 w-3 mt-0.5 shrink-0" />{t("pp.reco.othersHint")}</p>
              <div className="relative mb-3">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pp.reco.searchResident")} data-testid="reco-search"
                  className="w-full h-10 rounded-lg bg-white/[0.04] border border-white/10 pl-9 pr-3 text-sm" />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1.5">
                {manualCandidates.slice(0, 30).map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
                    <span className="text-slate-200">{r.full_name}{r.room ? ` · ${r.room}` : ""}</span>
                    <button onClick={() => decide(r.id, "add")} data-testid={`reco-manual-add-${r.id}`}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${picks[r.id] === "add" ? "bg-emerald-500 text-slate-900" : "bg-white/10 hover:bg-emerald-500/20 text-slate-200"}`}>
                      <Check className="h-3.5 w-3.5" />{picks[r.id] === "add" ? t("pp.reco.added") : t("pp.reco.add")}
                    </button>
                  </div>
                ))}
                {manualCandidates.length === 0 && <p className="text-xs text-slate-500 text-center py-3">—</p>}
              </div>
            </div>

            {/* Bilan de participation (présence + effet observé + note) */}
            <div className="mt-6 rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-4" data-testid="bilan-section">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-200"><ClipboardList className="h-4 w-4 text-teal-400" />{t("pp.bilan.title")}</div>
                <label className="text-xs text-slate-400 flex items-center gap-2">{t("pp.bilan.date")}
                  <input type="date" value={bilanDate} onChange={(e) => setBilanDate(e.target.value)} data-testid="bilan-date"
                    className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm text-slate-100" /></label>
              </div>
              {retainedIds.length === 0 ? (
                <p className="text-xs text-slate-500">{t("pp.bilan.noResidents")}</p>
              ) : (
                <div className="space-y-3">
                  {retainedIds.map((rid) => {
                    const info = nameById[rid] || {};
                    const p = part[rid] || { presence: "present", effect: "", note: "" };
                    return (
                      <div key={rid} className="rounded-lg bg-white/[0.03] border border-white/10 p-3" data-testid={`bilan-row-${rid}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <span className="font-semibold text-slate-100 text-sm">{info.name}{info.room ? ` · ${info.room}` : ""}</span>
                          <button onClick={() => savePart(rid)} data-testid={`bilan-save-${rid}`}
                            className="inline-flex items-center gap-1 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-900 px-3 py-1 text-xs font-semibold shrink-0"><Save className="h-3.5 w-3.5" />{t("pp.bilan.save")}</button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <select value={p.presence} onChange={(e) => setP(rid, { presence: e.target.value })} data-testid={`bilan-presence-${rid}`}
                            className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm text-slate-100">
                            {PRESENCES.map((x) => <option key={x} value={x} className="bg-[#0f2130]">{t(`pp.bilan.${x}`)}</option>)}
                          </select>
                          <select value={p.effect} disabled={p.presence !== "present"} onChange={(e) => setP(rid, { effect: e.target.value })} data-testid={`bilan-effect-${rid}`}
                            className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm text-slate-100 disabled:opacity-50">
                            <option value="" className="bg-[#0f2130]">{t("pp.bilan.effect")}…</option>
                            {EFFECTS.map((x) => <option key={x} value={x} className="bg-[#0f2130]">{t(`pp.bilan.effects.${x}`)}</option>)}
                          </select>
                          <input value={p.note || ""} onChange={(e) => setP(rid, { note: e.target.value })} placeholder={t("pp.bilan.note")} data-testid={`bilan-note-${rid}`}
                            className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm text-slate-100" />
                        </div>
                        {info.goalTitle && <div className="mt-1.5 text-xs text-slate-500">{t("pp.bilan.linkAction")} : {info.goalTitle}</div>}
                        {info.goal && p.presence === "present" && (p.effect === "tres_positif" || p.effect === "positif") && (
                          <div className="mt-2" data-testid={`bilan-actionsug-${rid}`}>
                            <button onClick={() => toggleActions(rid, info.goal)} data-testid={`bilan-proposeaction-${rid}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-teal-300 hover:text-teal-200">
                              <Check className="h-3.5 w-3.5" />{t("pp.bilan.proposeAction")}
                            </button>
                            {actSug[rid]?.open && (
                              <div className="mt-1.5 space-y-1">
                                {actSug[rid].loading ? <span className="text-xs text-slate-500">…</span> :
                                  (actSug[rid].actions || []).length === 0 ? <span className="text-xs text-slate-500">{t("pp.bilan.noPending")}</span> :
                                  actSug[rid].actions.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between gap-2 rounded bg-white/[0.03] px-2 py-1.5">
                                      <span className="text-xs text-slate-300">{a.text}</span>
                                      <button onClick={() => markActionDone(rid, info.goal, a.id)} data-testid={`bilan-markdone-${a.id}`}
                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/80 hover:bg-emerald-500 text-white px-2 py-0.5 text-[11px] font-semibold shrink-0"><Check className="h-3 w-3" />{t("pp.bilan.markDone")}</button>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
